import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatListOption, MatSelectionList } from '@angular/material/list';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { head, merge } from 'lodash-es';
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  scan,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { triggerIfSlow } from '@ipg/ngx-frontend-toolset';
import { GlobalLoadersService } from '../../global-loaders.service';
import {
  DEVICE_TYPE_LABELS_BY_CATEGORY,
  Device,
} from '../../models/devices.model';
import { MapFromPipe } from '../../pipes/map-from.pipe';
import { DevicesService } from '../../services/devices.service';
import { BaseDialog } from '../dialog/base-dialog';
import { ConfirmHandler } from '../dialog/dialog.model';

@Component({
  selector: 'app-device-selection-dialog',
  imports: [
    MatButton,
    MatDialogActions,
    MatDialogContent,
    MatDialogClose,
    MatDialogTitle,
    MatProgressSpinner,
    MatSelectionList,
    MatListOption,
    MatTooltip,
    MapFromPipe,
    MatProgressBarModule,
    MatFormField,
    MatInput,
    MatIcon,
    MatIconButton,
    MatPrefix,
    MatSuffix,
    FormsModule,
  ],
  templateUrl: './device-selection-dialog.component.html',
  styleUrl: './device-selection-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviceSelectionDialogComponent extends BaseDialog<{
  multiple: boolean;
  category: 'robot' | 'access-point';
  onConfirm?: ConfirmHandler;
}> {
  public static readonly LOADER_ID = 'client_devices_dialog_loader';
  private static readonly LOADER_CALLER_ID = 'client_devices_dialog_loader_caller';

  private readonly _dialogRef =
    inject<MatDialogRef<DeviceSelectionDialogComponent>>(MatDialogRef);
  private readonly _devices = inject(DevicesService);

  private readonly _fetch$ = new Subject<void>();

  private readonly _keywordInput$ = new Subject<string | null>();
  private readonly _debouncedKeyword$ = this._keywordInput$.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    tap(() => this._query.update((currentState) => ({ ...currentState, page: 1 })))
  );

  protected readonly keyword = signal<string | null>(null);

  private readonly _query = signal({
    page: 1,
    limit: 10,
  });

  private readonly _sort = signal({
    sortBy: 'label',
    sortOrder: 'asc',
  });

  private readonly _params = combineLatest({
    query: toObservable(this._query),
    sort: toObservable(this._sort),
    keyword: this._debouncedKeyword$.pipe(startWith(this.keyword())),
    __fetch: this._fetch$.pipe(startWith(undefined)),
  }).pipe(debounceTime(0));

  private readonly _paginatedDevices$ = this._params.pipe(
    tap(() => this.fetching.set(true)),
    switchMap(({ query, sort, keyword }) =>
      this._devices
        .getAll({
          params: {
            ...query,
            ...sort,
            ...(keyword ? { q: keyword.trim() } : null),
            attached: false,
            category: this.data.category,
          },
        })
        .pipe(
          takeUntilDestroyed(this._destroyRef),
          triggerIfSlow(() =>
            this.globalLoaders.setActive(
              DeviceSelectionDialogComponent.LOADER_CALLER_ID,
              DeviceSelectionDialogComponent.LOADER_ID
            )
          ),
          finalize(() =>
            this.globalLoaders.setInactive(
              DeviceSelectionDialogComponent.LOADER_CALLER_ID,
              DeviceSelectionDialogComponent.LOADER_ID
            )
          )
        )
    ),
    tap(() => this.fetching.set(false)),
    shareReplay(1)
  );

  private readonly _accumulatedDevices$ = this._paginatedDevices$.pipe(
    scan((acc, curr) => {
      if (curr.meta.page === 1) {
        return curr.items;
      }

      return [...acc, ...curr.items];
    }, [] as Device[])
  );

  private readonly _meta$ = this._paginatedDevices$.pipe(map(({ meta }) => meta));

  private readonly _paginatedMetadata = toSignal(this._meta$);

  private readonly _state = signal({
    multiple: false,
  });

  private readonly _destroyRef = inject(DestroyRef);

  protected readonly globalLoaders = inject(GlobalLoadersService);

  protected readonly loaderId = DeviceSelectionDialogComponent.LOADER_ID;

  protected readonly dataSource = toSignal(this._accumulatedDevices$, {
    initialValue: [],
  });

  protected readonly isLastPage = computed(() => this._paginatedMetadata()?.lastPage);

  protected readonly showLoadMore = computed(
    () => this.dataSource().length && !this.isLastPage()
  );

  protected readonly selection = viewChild.required(MatSelectionList);

  protected readonly selectedOptions = computed(() => this.selection().selectedOptions);

  protected readonly deviceMap = DEVICE_TYPE_LABELS_BY_CATEGORY;

  protected readonly fetching = signal(false);
  protected readonly submitted = signal(false);
  protected readonly loading = signal(false);

  protected readonly multiple = computed(() => this._state().multiple);
  protected readonly selectedCount = signal(0);

  constructor() {
    super();

    this._setState(this.data);
  }

  protected handleSelectionChange(): void {
    const count = this.selection().selectedOptions.selected.length;
    this.selectedCount.set(count);
  }

  loadMoreItems() {
    if (this.isLastPage() || this.fetching()) {
      return;
    }

    this._query.update((currentState) => ({
      ...currentState,
      page: currentState.page + 1,
    }));
  }

  override handleConfirm(): void {
    this.submitted.set(true);

    const selection = this.selection().selectedOptions.selected.map(
      (selected) => selected.value
    );

    if (this.data.onConfirm) {
      const callback$ = this.data.onConfirm(
        this.multiple() ? selection : head(selection)
      );

      const source$ = callback$.pipe(
        shareReplay(1),
        catchError((error) => {
          this.submitted.set(false);

          return throwError(() => error);
        })
      );

      const loading$ = merge(
        timer(1000).pipe(
          map(() => true),
          takeUntil(source$)
        ),

        source$.pipe(map(() => false))
      );

      loading$
        .pipe(
          tap((state) => this.loading.set(state)),
          filter((state) => !state),
          tap(() => this._dialogRef.close(true)),
          take(1)
        )
        .subscribe();

      return;
    }
  }

  handleSearch(value: string) {
    this.keyword.set(value.trim());
    this._keywordInput$.next(value.trim());
  }

  clearSearch() {
    this.keyword.set(null);
    this._keywordInput$.next(null);
  }

  private _setState(newState: Record<string, unknown>) {
    this._state.update((currentState) => ({
      ...currentState,
      ...newState,
    }));
  }
}
